/**
	* Measuring tool view
	* Handles: measuring
	* @module MeasuringTool
*/

/// Imports

// React
import React, {useState, useEffect, useCallback} from "react";

// OpenLayers
import OlMap from "ol/Map";
import Draw from "ol/interaction/Draw";
import Snap from "ol/interaction/Snap";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import Geometry from "ol/geom/Geometry";

// Tools
import { assetURL } from "lib/utilities/asset";
import { getSnapToleranceMultiplier } from "lib/utilities/map";
import {
	removeFeatureFromSource,
	formatFeature,
	getFeatures,
	deleteFeature
} from "lib/utilities/feature";

// Lib
import FeatureMode from "lib/enums/featuremode";
import MapToolMode from "lib/enums/maptoolmode";
import DrawToolInteractions from "lib/interfaces/drawtoolinteractions";

// Lang
import { getLocaleString } from "lib/localization/vendor";

// Styling
import StyleMeasureFeature from "styles/script/stylemeasurefeature";
import "styles/css/DrawTool.scss";
import "styles/css/MeasureTool.scss";

interface MeasuringToolProps {
	olMap:OlMap,
	currentToolMode:MapToolMode,
}

// Interactions are set in initialize()
const Interactions:DrawToolInteractions = {}; 

/*
	Measuring source & layer for measure features
*/
const measuringDrawingSource:VectorSource = new VectorSource();
const measuringLayer:VectorLayer<VectorSource> = new VectorLayer({
	source: measuringDrawingSource
});

export default function MeasuringTool({olMap, currentToolMode}:MeasuringToolProps) {
	// Set the measuring layer to the map
	olMap.set("measuring-layer", measuringLayer);
	
	const mapConfig:any = olMap.get("opus-config");
	
	// State hooks
	const [featureMode, setFeatureMode] = useState<FeatureMode>(FeatureMode.Polygon);
	const [drawnMeasureFeatures, setDrawnMeasureFeatures] = useState<Feature<Geometry>[]>([]);
		
	// Events defined from drawtool instance
	const onFeatureSavedNoSync:Function = (f:Feature<Geometry>) => _events.invoke("featuresaved-nosync", f);
	
	// Callback hooks
	const handleKeyDownCallback = useCallback((e:KeyboardEvent) => {
		handleKeyDown(e);
	}, [featureMode, currentToolMode]);
	
	const handleMapClickCallback = useCallback((e:any) => {
		handleMapClick(e);
	}, [featureMode, currentToolMode]);
	
	/*
		Initialize
	*/
	useEffect(() => {
		initialize();
	}, []);
	
	/*
		When feature mode changes or tool mode changes
	*/
	useEffect(() => {
		updateInteractionToggle();
		
		/*
			* Capture keyboard event for callback hook
			
			* Remove it as soon as it has been handled
			
			* This is so the changing state hooks aren't captured in a closure 
			and become blind to any changes.
		*/
		window.addEventListener("keydown", handleKeyDownCallback);
		window.addEventListener("keyup", handleKeyUpCallback);
		
		// Map click static feature handler
		olMap.on("click", handleMapClickCallback);
		
		return () => {
			window.removeEventListener("keydown", handleKeyDownCallback);
			window.removeEventListener("keyup", handleKeyUpCallback);
			olMap.un("click", handleMapClickCallback);
		};
	}, [featureMode, currentToolMode]);
	
	/*
		When the current tool mode changes or features drawn
	*/
	useEffect(() => {
		// Rebuild and clear the tooltip
		_ui.tooltip.rebuild();
		_ui.tooltip.hide();
	}, [currentToolMode, drawnMeasureFeatures]);
	
	/*
		When the current tool mode changes
	*/
	useEffect(() => {
		//clearDrawnMeasureFeatures();
		
		// This is better
		measuringLayer.setVisible(showMeasureTool());
	}, [currentToolMode]);
	
	useEffect(() => updateSnap(), [currentToolMode]);
	
	/*
		Initialize method
	*/
	const initialize = () => {
		// Setup interactions
		setupInteractions();
		setupMap();
		
		// Clicked on a measurement feature
		_events.on("clicked-measurement-feature", (feature:Feature<Geometry>) => {
			_ui.showNotice(getLocaleString("clickedmeasurefeature"));
			_events.invoke("tool-mode-set", MapToolMode.Measure);
		});
		
		/*
			Remove feature handler for measure tool
		*/
		_events.on('remove-feature', (feature:Feature<Geometry>) => {
			if(!isFeatureMine(feature))
				return;
			
			deleteFeature(feature, measuringDrawingSource);
		});
	}
	const setupMap = () => {
		olMap.addLayer(measuringLayer);
		
		olMap.addInteraction(Interactions.Polygon);
		olMap.addInteraction(Interactions.LineString);
		olMap.addInteraction(Interactions.Snap);
		
		measuringLayer.setStyle(StyleMeasureFeature);
	}
	const setupInteractions = () => {
		// Setup
		Interactions.Polygon = new Draw({
			source: measuringDrawingSource,
			type: "Polygon",
			snapTolerance: 8 * getSnapToleranceMultiplier(),
			style: StyleMeasureFeature
		});
		Interactions.LineString = new Draw({
			source: measuringDrawingSource,
			type: "LineString",
			snapTolerance: 8 * getSnapToleranceMultiplier(),
			style: StyleMeasureFeature
		});
		Interactions.Snap = new Snap({
			features: olMap.get("snap-features"),
			pixelTolerance: 8 * getSnapToleranceMultiplier()
		});
		
		// Events for the interactions
		Interactions.Polygon.on("drawstart", (e:any) => {
			const feature:Feature<Geometry> = e.feature;
			
			const featureChanged = (feature:Feature<Geometry>) => {
				formatFeature(feature, onFeatureFormatted);
			}
			
			clearInterval(Interactions.Polygon.get("draw-interval"));
			
			Interactions.Polygon.set("draw-interval", setInterval(featureChanged, 100, feature));
		});
		Interactions.LineString.on("drawstart", (e:any) => {
			const feature:Feature<Geometry> = e.feature;
			
			const featureChanged = (feature:Feature<Geometry>) => {
				formatFeature(feature, onFeatureFormatted);
			}
			
			clearInterval(Interactions.LineString.get("draw-interval"));
			
			Interactions.LineString.set("draw-interval", setInterval(featureChanged, 100, feature));
		});
		Interactions.Polygon.on("drawend", (e:any) => {
			e.feature.set("origin", 'measurement'); // its a measurement feature
			setDrawnMeasureFeatures(getFeatures(measuringDrawingSource));
			onFeatureSavedNoSync(e.feature);
		});
		Interactions.LineString.on("drawend", (e:any) => {
			e.feature.set("origin", 'measurement'); // its a measurement feature
			setDrawnMeasureFeatures(getFeatures(measuringDrawingSource));
			onFeatureSavedNoSync(e.feature);
		});
	}
	/*
		Abort drawing in progress
	*/
	const abortDrawing = () => {
		switch(featureMode) {
			case FeatureMode.Polygon:
				Interactions.Polygon.abortDrawing();
			break;
			case FeatureMode.LineString:
				Interactions.LineString.abortDrawing();
			break;
		}
	}
	/*
		Toggle interactions based on drawing mode and the active map tool
	*/
	const updateInteractionToggle = () => {
		Interactions.Polygon.setActive(
			featureMode == FeatureMode.Polygon &&
			currentToolMode == MapToolMode.Measure
		);
		Interactions.LineString.setActive(
			featureMode == FeatureMode.LineString &&
			currentToolMode == MapToolMode.Measure
		);
	}
	/*
		Feature formatted event
	*/
	const onFeatureFormatted = (feature:Feature<Geometry>) => {
		feature.set("get-zoom", ():number => olMap.getView().getZoom()); // So styled feature can get the zoom level
	}
	/*
		Update feature mode
	*/
	const updateFeatureMode = (mode:FeatureMode) => {
		setFeatureMode(mode);
	}
	/*
		Update snap
	*/
	const updateSnap = () => {
		const shouldSnap:boolean = !_ui.isSpaceDown() && currentToolMode != MapToolMode.Annotation;
		
		Interactions.Snap.setActive(shouldSnap);
	}
	
	/*
		Handle key down
	*/
	const handleKeyDown = (e:KeyboardEvent) => {
		// Something like an input box is selected so abort this handler
		if(_ui.hasFocus()) return;
		
		if(e.key == "Escape") {
			if(showMeasureTool()) {
				e.preventDefault();
				abortDrawing();
			}
		}
		
		updateSnap();
	}
	/*
		Handle key up
	*/
	const handleKeyUpCallback = (e:KeyboardEvent) => {
		// Something like an input box is selected so abort this handler
		if(_ui.hasFocus()) return;
		
		updateSnap();
	}
	/*
		Is this view meant to be active?
	*/
	const showMeasureTool = ():boolean => {
		return currentToolMode == MapToolMode.Measure || currentToolMode == MapToolMode.Select || currentToolMode == MapToolMode.MapLayers;
	}
	/*
		Clear the drawn features
	*/
	const clearDrawnMeasureFeatures = () => {
		measuringDrawingSource.clear();
		
		setDrawnMeasureFeatures(getFeatures(measuringDrawingSource));
	}
	/*
		Click clear the drawn features
	*/
	const clickClearMeasurementFeatures = () => {
		_ui.showConfirm(
			getLocaleString("clearconfirm", [getFeatures(measuringDrawingSource).length.toString()]), 
			() => clearDrawnMeasureFeatures()
		);
	}
	const handleMapClick = (e:any) => {
		if(showMeasureTool()) return;
		
		olMap.forEachFeatureAtPixel(e.pixel, (feature:Feature<Geometry>) => {
			if(feature.get("origin") != 'measurement') return;
			
			// Clicked on measurement feature
			_events.invoke("clicked-measurement-feature", feature);
		});
	}
	
	/**
		* View draw methods
	*/
	const drawFeatureModeToolButton = (mode:FeatureMode) => {
		const name:string = FeatureMode[mode];
		const className:string = `drawing-option drawing-option--${name.toLowerCase()} measuring-option measuring-option--${name.toLowerCase()} ${featureMode == mode ? ` drawing-option--selected ${_ui.getSelClassName()}` : ""}`;
		
		const tips:Map<string, string> = new Map<string, string>([
			["Polygon", "area"],
			["LineString", "distance"]
		]);
		const tip:string = tips.get(name);
		
		return (
			<div className={className} onClick={() => updateFeatureMode(mode)} data-tip={getLocaleString(tip)}>
				<img src={assetURL(`assets/images/${name.toLowerCase()}.png`)} />
			</div>
		)
	}
	const drawClearMeasuredFeaturesButton = () => {
		if(getFeatures(measuringDrawingSource).length < 1) return;
		
		return (
			<>
				<div 
					className="drawing-option drawing-option--bin measuring-option measuring-option--bin" 
					onClick={() => clickClearMeasurementFeatures()} 
					data-tip={`${getLocaleString("clear")} (⌫)`}
				>
					<img src={assetURL("assets/images/bin.png")} />
				</div>
			</>
		)
	}
	const drawMeasuringTool = () => {
		return (
			<>
				{drawFeatureModeToolButton(FeatureMode.Polygon)}
				{drawFeatureModeToolButton(FeatureMode.LineString)}
				{drawClearMeasuredFeaturesButton()}
			</>
		)
	}
	
	return (
		<>
			{currentToolMode == MapToolMode.Measure && drawMeasuringTool()}
		</>
	)
}

function isFeatureMine(feature:Feature<Geometry>) {
	switch(feature.get('origin')) {
		case 'measurement':
			return true;
		default:
			return false;
	}
}